import React, { useState } from "react"
import { motion } from "framer-motion"
import styled, { css } from "styled-components"

import ROCThumbnail from "@images/abcares/ROC_solid_video_thumbnail2.png"
import PeopleWalking from "@images/abcares/AB_cares_generic.jpg"
import BGC from "@images/abcares/BGC Logo Lockup.jpg"

const size = {
  tablet: 950,
  mobile: 600,
}
const below = Object.keys(size).reduce((acc, label) => {
  acc[label] = (...args) => css`
    @media (max-width: ${size[label]}px) {
      ${css(...args)};
    }
  `
  return acc
}, {})

const WalkingImage = styled(motion.img)`
  position: absolute;
  top: 0;
  left: 0;
  box-shadow: 0px 8px 16px rgba(0, 0, 0, 0.25);
  border-radius: 4px;
  width: 100%;
`

const BGCImage = styled(motion.img)`
  position: absolute;
  top: 0;
  left: 0;
  border-radius: 4px;
  box-shadow: 0px 8px 16px rgba(0, 0, 0, 0.25);
  width: 100%;
`
const ROCImage = styled(motion.img)`
  position: absolute;
  top: 0;
  left: 0;
  border-radius: 4px;
  box-shadow: 0px 8px 16px rgba(0, 0, 0, 0.25);
  width: 100%;
`

const InvisibleButton = styled.button`
  width: 100%;
  background: none;
  color: inherit;
  border: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
  outline: inherit;
`

const ImagesContainer = styled(motion.div)`
  width: 50%;
  margin-right: 6.38%;

  ${below.tablet`
    width:100%;
    margin-bottom:38px;
    margin-right:0;
    `}
  ${below.mobile`
  margin-bottom:14px;
    `}
`

const SliderContainer = styled.div`
  padding: 0 3.33% 7.77%;
  display: flex;
  @media (min-width: 1440px) {
    padding: 0 48px 112px;
  }
  ${below.tablet`
    padding: 0 48px 64px;
    flex-direction:column;
    `}
  ${below.mobile`
  
    padding: 0 24px 64px;
    `}
`

const SliderText = styled.div`
  width: 50%;

  ${below.tablet`
  width:100%;
  `}

  p {
    margin-bottom: 0;

    ${below.mobile`
    max-width:500px;

  `}

    .highlight {
      background: #ffefb4;
    }
    .highlight:hover {
      background: var(--horizon-yellow);
    }
  }
`

const Controls = styled.div`
  margin-top: 46px;
  display: flex;
  justify-content: center;
  ${below.mobile`
margin-top:22px;
  `}
`

const Slide0 = styled.div`
  cursor: pointer;
  margin: 0 8px;
  img {
    padding: 20px 0;
  }
`
const Slide1 = styled.div`
  cursor: pointer;
  margin: 0 8px;
  img {
    padding: 20px 0;
  }
`
const Slide2 = styled.div`
  cursor: pointer;
  margin: 0 8px;
  img {
    padding: 20px 0;
  }
`

const Span0 = styled.span`
  background: #ffefb4;
  ${({ visibleImage }) =>
    visibleImage === 0 &&
    `
    background: var(--horizon-yellow);
  `}
`
const Span1 = styled.span`
  background: #ffefb4;

  ${({ visibleImage }) =>
    visibleImage === 1 &&
    `
    background: var(--horizon-yellow);
  `}
`
const Span2 = styled.span`
  background: #ffefb4;

  ${({ visibleImage }) =>
    visibleImage === 2 &&
    `
    background: var(--horizon-yellow);
  `}
`

const ImageWrapper = styled.div`
  position: relative;
  height: 0;
  padding-top: calc(408 / 672 * 100%);
`

const variants = {
  enter: { opacity: 0, y: 50 },
  center: { opacity: 1, y: 0, transition: { duration: 0.3 } },
  exit: { opacity: 1, x: 100 },
}

const SlideIndicator = styled.div`
  height: 4px;
  width: 16px;
  background: var(--btn-light-blue);
  margin: 20px 0;

  &.active {
    width: 48px;
    height: 4px;
    background: var(--pacific-blue);
  }
`

function Slider({ videoToggled, setVideoToggled }) {
  const [visibleImage, setVisibleImage] = useState(0)
  return (
    <SliderContainer>
      <ImagesContainer>
        <ImageWrapper>
          {visibleImage === 0 && (
            <InvisibleButton onClick={() => setVideoToggled(!videoToggled)}>
              <ROCImage
                src={ROCThumbnail}
                alt="ROC video thumbnail image"
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
              />
            </InvisibleButton>
          )}

          {visibleImage === 1 && (
            <BGCImage
              src={BGC}
              alt="Boys and Girls Club image"
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
            />
          )}
          {visibleImage === 2 && (
            <WalkingImage
              src={PeopleWalking}
              alt="people walking"
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
            />
          )}
        </ImageWrapper>
        <Controls>
          <Slide0 onClick={() => setVisibleImage(0)}>
            {visibleImage === 0 ? (
              <SlideIndicator className="active" />
            ) : (
              <SlideIndicator />
            )}
          </Slide0>

          <Slide1 onClick={() => setVisibleImage(1)}>
            {visibleImage === 1 ? (
              <SlideIndicator className="active" />
            ) : (
              <SlideIndicator />
            )}
          </Slide1>
          <Slide2 onClick={() => setVisibleImage(2)}>
            {visibleImage === 2 ? (
              <SlideIndicator className="active" />
            ) : (
              <SlideIndicator />
            )}
          </Slide2>
        </Controls>
      </ImagesContainer>
      <SliderText>
        <p>
          With every loan closed, Atlantic Bay makes a contribution to our
          Corporate Community Collaborators like the{" "}
          <Span0
            role="button"
            tabIndex={0}
            onMouseEnter={() => setVisibleImage(0)}
            visibleImage={visibleImage}
          >
            Roc Solid Foundation that helps build hope for kids and families
            fighting cancer.
          </Span0>{" "}
          You are helping pave the way toward{" "}
          <Span1
            role="button"
            tabIndex={0}
            onMouseEnter={() => setVisibleImage(1)}
            visibleImage={visibleImage}
          >
            a better tomorrow for our youth by supporting the Boys and Girls
            Club of SEVA.
          </Span1>{" "}
          And our commitment doesn’t stop with our corporate sponsorships; we
          donate time, funds, and resources to a{" "}
          <Span2
            role="button"
            tabIndex={0}
            onMouseEnter={() => setVisibleImage(2)}
            visibleImage={visibleImage}
          >
            variety of nonprofits and organizations
          </Span2>{" "}
          as we continue growing our footprint.
        </p>
      </SliderText>
    </SliderContainer>
  )
}

export default Slider
